import React, { useState, useEffect } from "react";
import { useParams, useNavigate, NavLink } from "react-router-dom";
import arService from "../../../../../services/arService";
import { hasPermission } from "../../../../../utils/permission";
import "./ReceiptView.css";
import { showMessageBox } from '../../../../site/fn';

const isPendingApprovalStatus = (status) =>
  status === "Pending Approval" || status === "Pending";

const ReceiptView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const canEdit = hasPermission("ar-receipt", "set");
  const canApprove = hasPermission("ar-receipt", "approve");

  useEffect(() => {
    fetchReceipt();
  }, [id]);

  const fetchReceipt = async () => {
    try {
      setIsLoading(true);
      const data = await arService.getReceiptById(id);
      setReceipt(data);
    } catch (error) {
      console.error("Error fetching receipt:", error);
      showMessageBox("Failed to fetch receipt details.", "error");
      navigate("/ar/receipts");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "N/A";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹ 0.00";
    return `₹ ${parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getStatusBadge = (approvalStatus, isDraft) => {
    if (isDraft || approvalStatus === "Draft") {
      return <span className="status-badge draft">Draft</span>;
    }
    if (approvalStatus === "Approved") {
      return <span className="status-badge approved">Approved</span>;
    }
    if (approvalStatus === "Rejected") {
      return <span className="status-badge rejected">Rejected</span>;
    }
    if (isPendingApprovalStatus(approvalStatus)) {
      return <span className="status-badge pending">Pending Approval</span>;
    }
    return <span className="status-badge active">Active</span>;
  };

  const handleApprove = async () => {
    if (!window.confirm(`Are you sure you want to approve Receipt ${receipt.receipt_no}?`)) {
      return;
    }

    const remark = window.prompt("Enter approval remark (optional):") ?? "";

    try {
      await arService.approveReceipt(id, remark);
      showMessageBox("Receipt approved successfully!", "success");
      fetchReceipt();
    } catch (error) {
      console.error("Error approving receipt:", error);
      showMessageBox(error.message || "Failed to approve receipt. Please try again.", "error");
    }
  };

  const handleReject = async () => {
    const remark = window.prompt(`Enter reason for rejecting Receipt ${receipt.receipt_no}:`);
    if (!remark || remark.trim() === "") {
      showMessageBox("Rejection reason is required!", "error");
      return;
    }

    try {
      await arService.rejectReceipt(id, remark.trim());
      showMessageBox("Receipt rejected successfully!", "success");
      fetchReceipt();
    } catch (error) {
      console.error("Error rejecting receipt:", error);
      showMessageBox(error.message || "Failed to reject receipt. Please try again.", "error");
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading receipt...</p>
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="error-container">
        <p>Receipt not found.</p>
        <NavLink to="/ar/receipts">
          <button className="btn-back">Back to Receipts</button>
        </NavLink>
      </div>
    );
  }

  if (receipt.approval_status === "Approved") {
    navigate(`/ar/receipts/${id}/print`, { replace: true });
    return null;
  }

  const allocations = receipt.invoice_allocations || [];
  const totalAllocated = allocations.reduce(
    (sum, alloc) => sum + parseFloat(alloc.allocated_amount || 0),
    0
  );
  const getAllocNetApplied = (alloc) =>
    parseFloat(alloc.net_allocated || alloc.net_allocation || 0) +
    parseFloat(alloc.gst_on_hold_total || 0);
  const totalNetApplied = allocations.reduce(
    (sum, alloc) => sum + getAllocNetApplied(alloc),
    0
  );
  const isEditable =
    receipt.is_draft ||
    receipt.approval_status === "Draft" ||
    receipt.approval_status === "Rejected" ||
    (receipt.approval_status === "Approved" && receipt.pending_allocation);

  return (
    <div className="receipt-view-container">
      <div className="view-header no-print">
        <div className="header-left">
          <NavLink to="/ar/receipts">
            <button className="btn-back">← Back to List</button>
          </NavLink>
        </div>
        <div className="header-right">
          {canEdit && isEditable && (
            <NavLink to={`/ar/receipts/${id}/edit`}>
              <button className="btn-edit">✏️ Edit</button>
            </NavLink>
          )}
          {canApprove && isPendingApprovalStatus(receipt.approval_status) && (
            <>
              <button className="btn-approve" onClick={handleApprove}>
                ✓ Approve
              </button>
              <button className="btn-reject" onClick={handleReject}>
                ✗ Reject
              </button>
            </>
          )}
        </div>
      </div>

      <div className="receipt-document">
        <div className="document-header">
          <h1>RECEIPT</h1>
          <div className="doc-info">
            <div className="doc-number">
              <strong>Receipt No:</strong> {receipt.receipt_no || "N/A"}
            </div>
            <div className="doc-status">
              {getStatusBadge(receipt.approval_status, receipt.is_draft)}
            </div>
          </div>
        </div>

        <div className="info-section">
          <h3>Receipt Details</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Receipt Date:</label>
              <span>{formatDate(receipt.receipt_date)}</span>
            </div>
            <div className="info-item">
              <label>Customer:</label>
              <span>{receipt.customer_name || "N/A"}</span>
            </div>
            <div className="info-item">
              <label>Payment Mode:</label>
              <span>{receipt.payment_mode || "N/A"}</span>
            </div>
            <div className="info-item">
              <label>Reference No:</label>
              <span>{receipt.reference_no || "N/A"}</span>
            </div>
            <div className="info-item">
              <label>Bank Name:</label>
              <span>{receipt.bank_name || "N/A"}</span>
            </div>
            <div className="info-item">
              <label>Branch Name:</label>
              <span>{receipt.branch_name || "N/A"}</span>
            </div>
            <div className="info-item">
              <label>Cheque Date:</label>
              <span>{formatDate(receipt.cheque_date)}</span>
            </div>
            <div className="info-item full-width">
              <label>Remarks:</label>
              <span>{receipt.remarks || "N/A"}</span>
            </div>
          </div>
        </div>

        {allocations.length > 0 && (
          <div className="info-section">
            <h3>Invoice Allocations</h3>
            <div className="allocations-table-wrap">
              <table className="allocations-table">
                <thead>
                  <tr>
                    <th>Invoice No</th>
                    <th>Invoice Date</th>
                    <th className="text-right">Invoice Amount</th>
                    <th className="text-right">Allocated</th>
                    <th className="text-right">Balance Allocation</th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.map((alloc) => (
                    <tr key={alloc.id || alloc.invoice_id}>
                      <td>{alloc.invoice_no || "N/A"}</td>
                      <td>{formatDate(alloc.invoice_date)}</td>
                      <td className="text-right">{formatCurrency(alloc.invoice_total)}</td>
                      <td className="text-right">{formatCurrency(alloc.allocated_amount)}</td>
                      <td className="text-right">
                        {formatCurrency(getAllocNetApplied(alloc))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="info-section">
          <h3>Summary</h3>
          <div className="amount-table">
            <div className="amount-row">
              <span>Receipt Amount</span>
              <strong>{formatCurrency(receipt.amount || receipt.receipt_amount)}</strong>
            </div>
            <div className="amount-row">
              <span>Total Allocated</span>
              <strong>{formatCurrency(totalAllocated)}</strong>
            </div>
            <div className="amount-row total">
              <span>Total Balance Allocation</span>
              <strong>{formatCurrency(totalNetApplied)}</strong>
            </div>
          </div>
        </div>

        {isPendingApprovalStatus(receipt.approval_status) && (
          <div className="info-section">
            <h3>Approval</h3>
            <p className="reason-text">
              This receipt is pending approval and cannot be edited until it is approved or rejected.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceiptView;
